/* ==========================================================================
   STELBO — Filtrare si sortare (comportament)
   --------------------------------------------------------------------------
   Regula acestui fisier: nu preia NIMIC din logica Dawn. `assets/facets.js`
   ramane singurul care face fetch, rescrie grila si impinge in history.
   Aici se intampla doar trei lucruri:

     1. controalele native (checkbox, select, campurile de pret) primesc o
        interfata desenata peste ele, iar valoarea se scrie inapoi in ele;
     2. sheet-ul de pe mobil capata taburi si gest de tragere;
     3. o stare pur vizuala (randul de pastile active, ascuns cand e gol).

   De ce elemente custom si nu un `DOMContentLoaded` cu `querySelectorAll`:
   `facets.js` rescrie `innerHTML`-ul fiecarui `.js-filter` dupa fiecare
   filtrare. Un element custom se re-initializeaza singur cand parserul il
   recreeaza; o initializare unica la incarcare s-ar pierde la prima apasare.

   1. Utilitare
   2. <st-price-slider>
   3. <st-sort>
   4. Sheet-ul de pe mobil
   5. Randul de pastile
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     1. UTILITARE
     ======================================================================== */

  var LANG = document.documentElement.lang || 'ro-RO';

  var NF = (function () {
    try {
      return new Intl.NumberFormat(LANG, { maximumFractionDigits: 0 });
    } catch (e) {
      return { format: function (v) { return String(v); } };
    }
  })();

  /* Formatul de bani al magazinului e "8.900,00" (punct la mii, virgula la
     zecimale), dar tema poate fi mutata oricand pe formatul cu punct. Nu
     ghicim dupa setari: ne uitam la ultimul separator din sir si decidem daca
     ce urmeaza dupa el sunt zecimale. */
  function parseMoney(raw) {
    if (raw == null) return NaN;
    var s = String(raw).trim();
    if (!s) return NaN;
    s = s.replace(/[^\d.,-]/g, '');
    if (!s) return NaN;

    var lastDot = s.lastIndexOf('.');
    var lastComma = s.lastIndexOf(',');
    var cut = Math.max(lastDot, lastComma);

    if (cut > -1) {
      var tail = s.slice(cut + 1);
      if (tail.length > 0 && tail.length <= 2 && /^\d+$/.test(tail)) {
        // separator zecimal
        return parseFloat(s.slice(0, cut).replace(/[.,]/g, '') + '.' + tail);
      }
    }
    return parseFloat(s.replace(/[.,]/g, ''));
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  /* Un `input` care bubble-uieste pana la formular, acolo unde `facets.js`
     asculta si porneste fetch-ul (debounce 800 ms). */
  function notifyForm(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  var CARET =
    '<svg class="st-caret" viewBox="0 0 10 6" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M1 1.25 5 4.75l4-3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var CHECK =
    '<svg viewBox="0 0 12 9" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M1 4.6 4.3 8 11 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ========================================================================
     2. <st-price-slider>
     Doua thumb-uri peste campurile text ale Dawn. Campurile raman sursa de
     adevar: ele au `name="filter.v.price.gte|lte"` si ele ajung in FormData.
     Sliderul doar le scrie, apoi lasa formularul sa se ocupe de rest.
     ======================================================================== */

  var PriceSlider = (function () {
    function StPriceSlider() {
      return Reflect.construct(HTMLElement, [], StPriceSlider);
    }

    StPriceSlider.prototype = Object.create(HTMLElement.prototype);
    StPriceSlider.prototype.constructor = StPriceSlider;
    Object.setPrototypeOf(StPriceSlider, HTMLElement);

    StPriceSlider.prototype.connectedCallback = function () {
      if (this.stReady) return;

      this.minRange = this.querySelector('[data-st-range="min"]');
      this.maxRange = this.querySelector('[data-st-range="max"]');
      this.fill = this.querySelector('[data-st-fill]');
      this.outMin = this.querySelector('[data-st-out="min"]');
      this.outMax = this.querySelector('[data-st-out="max"]');

      if (!this.minRange || !this.maxRange) return;

      this.rmax = Number(this.dataset.max || 0);
      this.currency = this.dataset.currency || '';

      // Fara plaja nu exista ce filtra (toate produsele au pretul 0).
      if (!this.rmax || this.rmax <= 0) {
        this.hidden = true;
        return;
      }

      // Campurile text stau in `<price-range>`, frate cu elementul asta.
      var wrap = this.closest('.st-price') || this.parentElement;
      var fields = wrap ? wrap.querySelectorAll('price-range input') : [];
      this.fieldMin = fields[0] || null;
      this.fieldMax = fields[1] || null;

      this.onRange = this.handleRange.bind(this);
      this.minRange.addEventListener('input', this.onRange);
      this.maxRange.addEventListener('input', this.onRange);

      if (this.fieldMin) this.fieldMin.addEventListener('input', this.syncFromFields.bind(this));
      if (this.fieldMax) this.fieldMax.addEventListener('input', this.syncFromFields.bind(this));

      this.paint();
      this.stReady = true;
    };

    /* Nu lasam thumb-urile sa se incruciseze si ridicam deasupra pe cel care
       risca sa fie acoperit, altfel la capatul din dreapta nu se mai poate
       apuca minimul. */
    StPriceSlider.prototype.handleRange = function (event) {
      var lo = Number(this.minRange.value);
      var hi = Number(this.maxRange.value);

      if (lo > hi) {
        if (event.target === this.minRange) {
          this.minRange.value = hi;
          lo = hi;
        } else {
          this.maxRange.value = lo;
          hi = lo;
        }
      }

      this.writeFields(lo, hi);
      this.paint();
    };

    StPriceSlider.prototype.syncFromFields = function () {
      var lo = this.fieldMin ? parseMoney(this.fieldMin.value) : NaN;
      var hi = this.fieldMax ? parseMoney(this.fieldMax.value) : NaN;

      this.minRange.value = clamp(isNaN(lo) ? 0 : Math.round(lo), 0, this.rmax);
      this.maxRange.value = clamp(isNaN(hi) ? this.rmax : Math.round(hi), 0, this.rmax);
      this.paint();
    };

    /* Plaja completa inseamna "fara filtru": golim campurile, ca sa nu ramana
       `filter.v.price.gte=0&filter.v.price.lte=8900` agatat de URL. */
    StPriceSlider.prototype.writeFields = function (lo, hi) {
      var full = lo <= 0 && hi >= this.rmax;
      if (this.fieldMin) this.fieldMin.value = full ? '' : String(lo);
      if (this.fieldMax) this.fieldMax.value = full ? '' : String(hi);

      // `<price-range>` isi recalculeaza limitele la `change`.
      if (this.fieldMin) this.fieldMin.dispatchEvent(new Event('change', { bubbles: false }));
      if (this.fieldMax) this.fieldMax.dispatchEvent(new Event('change', { bubbles: false }));
    };

    StPriceSlider.prototype.paint = function () {
      var lo = Number(this.minRange.value);
      var hi = Number(this.maxRange.value);
      var span = this.rmax || 1;

      if (this.fill) {
        this.fill.style.left = (lo / span) * 100 + '%';
        this.fill.style.right = 100 - (hi / span) * 100 + '%';
      }

      if (this.outMin) this.outMin.textContent = NF.format(lo) + (this.currency ? ' ' + this.currency : '');
      if (this.outMax) this.outMax.textContent = NF.format(hi) + (this.currency ? ' ' + this.currency : '');

      // Cand minimul urca in jumatatea de sus, el trebuie sa fie deasupra.
      var minOnTop = lo > span / 2;
      this.minRange.style.zIndex = minOnTop ? '4' : '3';
      this.maxRange.style.zIndex = minOnTop ? '3' : '4';
    };

    return StPriceSlider;
  })();

  /* ========================================================================
     3. <st-sort>
     Listbox desenat peste `<select name="sort_by">`. Selectul ramane in
     formular si primeste valoarea, apoi un `input` care bubble-uieste —
     exact evenimentul pe care l-ar fi produs si alegerea nativa.
     ======================================================================== */

  var Sort = (function () {
    function StSort() {
      return Reflect.construct(HTMLElement, [], StSort);
    }

    StSort.prototype = Object.create(HTMLElement.prototype);
    StSort.prototype.constructor = StSort;
    Object.setPrototypeOf(StSort, HTMLElement);

    StSort.prototype.connectedCallback = function () {
      if (this.stReady) return;

      this.select = this.querySelector('select');
      if (!this.select || !this.select.options.length) return;

      this.buildUI();
      this.classList.add('is-enhanced');

      this.onDocClick = this.handleDocClick.bind(this);
      this.onKey = this.handleKey.bind(this);
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('keydown', this.onKey);

      this.stReady = true;
    };

    StSort.prototype.disconnectedCallback = function () {
      document.removeEventListener('click', this.onDocClick);
    };

    StSort.prototype.buildUI = function () {
      var self = this;
      var opts = Array.prototype.slice.call(this.select.options);
      var current = this.select.options[this.select.selectedIndex];

      this.btn = document.createElement('button');
      this.btn.type = 'button';
      this.btn.className = 'st-sort__btn';
      this.btn.setAttribute('aria-haspopup', 'listbox');
      this.btn.setAttribute('aria-expanded', 'false');
      this.btn.innerHTML = '<span class="st-sort__cur"></span>' + CARET;
      this.label = this.btn.querySelector('.st-sort__cur');
      this.label.textContent = current ? current.text : '';

      this.menu = document.createElement('div');
      this.menu.className = 'st-sort__menu';
      this.menu.setAttribute('role', 'listbox');

      opts.forEach(function (opt) {
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'st-sort__item' + (opt.selected ? ' is-on' : '');
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        item.dataset.value = opt.value;
        item.innerHTML = '<span>' + opt.text + '</span>' + CHECK;
        item.addEventListener('click', function () {
          self.choose(opt.value, opt.text);
        });
        self.menu.appendChild(item);
      });

      this.btn.addEventListener('click', function (event) {
        event.stopPropagation();
        self.toggle(!self.classList.contains('is-open'));
      });

      this.appendChild(this.btn);
      this.appendChild(this.menu);
    };

    StSort.prototype.toggle = function (open) {
      this.classList.toggle('is-open', open);
      this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    StSort.prototype.choose = function (value, text) {
      this.label.textContent = text;
      Array.prototype.forEach.call(this.menu.children, function (item) {
        var on = item.dataset.value === value;
        item.classList.toggle('is-on', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.toggle(false);

      if (this.select.value === value) return;
      this.select.value = value;
      notifyForm(this.select);
    };

    StSort.prototype.handleDocClick = function (event) {
      if (!this.contains(event.target)) this.toggle(false);
    };

    StSort.prototype.handleKey = function (event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.toggle(false);
        this.btn.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!this.classList.contains('is-open')) return;

      event.preventDefault();
      var items = Array.prototype.slice.call(this.menu.children);
      var at = items.indexOf(document.activeElement);
      var next = event.key === 'ArrowDown' ? at + 1 : at - 1;
      if (next < 0) next = items.length - 1;
      if (next >= items.length) next = 0;
      items[next].focus();
    };

    return StSort;
  })();

  /* ========================================================================
     4. SHEET-UL DE PE MOBIL
     Doua lucruri de stiut despre `MenuDrawer` (assets/global.js), parintele
     obligatoriu al formularului mobil:

       - `bindEvents()` leaga un handler de inchidere pe FIECARE `<button>`
         dinauntru. Taburile sunt butoane, deci un tab ar inchide sertarul.
         Le oprim in faza de captura, unde apucam evenimentul inaintea
         handlerului lui Dawn, si tot acolo facem comutarea.
       - `closeMenuDrawer` scoate `overflow-hidden-mobile` de pe `<body>`,
         deci blocarea derularii e deja rezolvata; nu adaugam alta.
     ======================================================================== */

  function initSheet(root) {
    var drawer = root.querySelector('.st-bar__drawer');
    if (!drawer || drawer.stSheetReady) return;

    var details = drawer.querySelector('details');
    var summary = drawer.querySelector('summary');
    var panel = drawer.querySelector('[data-st-panel]');
    if (!details || !summary || !panel) return;

    var tabs = drawer.querySelector('[data-st-tabs]');
    var ink = drawer.querySelector('[data-st-ink]');

    /* ---- taburi ---------------------------------------------------------- */

    function activate(name) {
      if (!tabs) return;
      var buttons = tabs.querySelectorAll('[data-st-tab]');
      Array.prototype.forEach.call(buttons, function (btn) {
        var on = btn.dataset.stTab === name;
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-st-pane]'), function (pane) {
        pane.hidden = pane.dataset.stPane !== name;
      });
      moveInk();
    }

    function moveInk() {
      if (!tabs || !ink) return;
      var active = tabs.querySelector('.st-sheet__tab.is-on');
      if (!active) return;
      ink.style.width = active.offsetWidth + 'px';
      ink.style.transform = 'translateX(' + active.offsetLeft + 'px)';
    }

    if (tabs) {
      // Captura: inaintea handlerului de inchidere pe care Dawn il pune
      // direct pe buton.
      drawer.addEventListener(
        'click',
        function (event) {
          var tab = event.target.closest('[data-st-tab]');
          if (!tab || !drawer.contains(tab)) return;
          event.preventDefault();
          event.stopPropagation();
          activate(tab.dataset.stTab);
        },
        true
      );
    }

    /* ---- deschidere / inchidere ------------------------------------------ */

    function isOpen() {
      return details.classList.contains('menu-opening');
    }

    function openSheet(tabName) {
      if (!isOpen()) summary.click();
      if (tabName) {
        // Lasam sertarul sa primeasca `menu-opening` inainte de a masura.
        requestAnimationFrame(function () {
          activate(tabName);
        });
      } else {
        requestAnimationFrame(moveInk);
      }
    }

    var sortBtn = root.querySelector('[data-st-open-sort]');
    if (sortBtn) {
      sortBtn.addEventListener('click', function () {
        openSheet(tabs ? 'sort' : null);
      });
    }

    summary.addEventListener('click', function () {
      requestAnimationFrame(moveInk);
    });

    /* ---- gest de tragere -------------------------------------------------
       Pragul e in pixeli SAU in viteza: o miscare scurta si rapida inchide,
       la fel ca in aplicatiile native. Sub prag, panoul se intoarce la loc. */

    var head = drawer.querySelector('.st-sheet__head');
    var grip = drawer.querySelector('[data-st-grip]');
    var handles = [head, grip].filter(Boolean);
    var startY = 0;
    var startT = 0;
    var delta = 0;
    var dragging = false;

    function down(event) {
      if (!isOpen()) return;
      if (event.target.closest('.st-sheet__close')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      dragging = true;
      startY = event.clientY;
      startT = event.timeStamp;
      delta = 0;
      panel.classList.add('is-dragging');
      // Fara captura, degetul care iese din antet nu mai trimite `pointermove`.
      // Poate esua daca pointerul nu mai e activ — atunci mergem mai departe.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (e) {
        /* ignorat intentionat */
      }
    }

    function move(event) {
      if (!dragging) return;
      delta = Math.max(0, event.clientY - startY);
      panel.style.transform = 'translateY(' + delta + 'px)';
    }

    function up(event) {
      if (!dragging) return;
      dragging = false;
      panel.classList.remove('is-dragging');
      panel.style.transform = '';

      var elapsed = Math.max(1, event.timeStamp - startT);
      var velocity = delta / elapsed; // px/ms
      if (delta > 110 || (velocity > 0.6 && delta > 30)) summary.click();
    }

    handles.forEach(function (el) {
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
    });

    window.addEventListener('resize', moveInk);
    requestAnimationFrame(moveInk);

    drawer.stSheetReady = true;
  }

  /* ========================================================================
     5. RANDUL DE PASTILE
     "Sterge tot" e mereu in DOM, deci randul nu e niciodata `:empty`. Numaram
     pastilele reale si punem clasa noi. `facets.js` rescrie `innerHTML`-ul
     acestor containere la fiecare filtrare, de unde observatorul.
     ======================================================================== */

  function initPills(root) {
    var rows = root.querySelectorAll('.st-pills, .st-bar__pills');
    if (!rows.length) return;

    function refresh(row) {
      var count = row.querySelectorAll('facet-remove:not(.active-facets__button-wrapper)').length;
      row.classList.toggle('is-empty', count === 0);
    }

    Array.prototype.forEach.call(rows, function (row) {
      refresh(row);
      if (row.stPillsReady) return;
      new MutationObserver(function () {
        refresh(row);
      }).observe(row, { childList: true });
      row.stPillsReady = true;
    });
  }

  /* ========================================================================
     PORNIRE
     ======================================================================== */

  if (!window.customElements.get('st-price-slider')) {
    window.customElements.define('st-price-slider', PriceSlider);
  }
  if (!window.customElements.get('st-sort')) {
    window.customElements.define('st-sort', Sort);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.st-facets'), function (root) {
      initSheet(root);
      initPills(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
